import { useLiveData } from "@/lib/useLiveData";
import { PageHeader, KpiCard, Loader } from "@/crm/ui";
import LiveBadge from "@/crm/components/LiveBadge";
import { useAuth } from "@/context/CrmAuth";
import {
  Users, PhoneCall, Mail, CalendarClock, Trophy, XCircle, Flame, Clock,
  Building2, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

const COLORS = ["#4f46e5", "#059669", "#f59e0b", "#8b5cf6", "#0ea5e9", "#ec4899", "#ef4444", "#14b8a6", "#64748b", "#f43f5e", "#eab308"];

export default function Dashboard() {
  const { user } = useAuth();
  const { data, refreshing, lastUpdated, refresh } = useLiveData("/dashboard/commercial", { interval: 15000 });

  if (!data) return <Loader />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title={`Bonjour ${user?.name?.split(" ")[0]} 👋`} subtitle="Voici votre activité commerciale du jour"
        actions={<LiveBadge refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <KpiCard testid="kpi-prospects" label="Prospects attribués" value={k.prospects} icon={Building2} accent="indigo" />
        <KpiCard testid="kpi-nouveaux" label="Nouveaux prospects" value={k.nouveaux} icon={Users} accent="violet" />
        <KpiCard testid="kpi-appels" label="Appels aujourd'hui" value={k.appels_today} icon={PhoneCall} accent="sky" />
        <KpiCard testid="kpi-emails" label="Emails envoyés" value={k.emails} icon={Mail} accent="emerald" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <KpiCard testid="kpi-retard" label="Tâches en retard" value={k.en_retard} icon={Clock} accent="rose" />
        <KpiCard testid="kpi-rappels" label="Rappels" value={k.rappels} icon={Flame} accent="amber" />
        <KpiCard testid="kpi-rdv" label="Rendez-vous" value={k.rdv} icon={CalendarClock} accent="indigo" />
        <KpiCard testid="kpi-interesses" label="Intéressés" value={k.interesses} icon={FileText} accent="emerald" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <KpiCard testid="kpi-propositions" label="Propositions" value={k.propositions} icon={FileText} accent="amber" />
        <KpiCard testid="kpi-gagnes" label="Ventes gagnées" value={k.gagnes} icon={Trophy} accent="emerald" />
        <KpiCard testid="kpi-perdus" label="Ventes perdues" value={k.perdus} icon={XCircle} accent="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Activités des 7 derniers jours</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Prospects par statut</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={95} label={(e) => e.count}>
                {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {data.by_status.map((s, i) => (
              <span key={s.status} className="text-xs text-slate-600 flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.status}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
